/**
 * Position: the full state of a xiangqi game at one point in time.
 *
 * This is a plain mutable container. Higher-level game logic (move history for
 * repetition detection, PGN, etc.) is layered on top in `game.ts`.
 */

import {
  Color,
  NUM_SQUARES,
  PieceType,
  fileOf,
  rankOf,
  type Move,
  type Piece,
  type Square,
} from "./types.js";

export class Position {
  /** `board[sq]` is the piece on that square, or `null` if empty. */
  readonly board: (Piece | null)[];
  sideToMove: Color;
  /**
   * Halfmove clock: plies since the last capture. Used by the 60-move
   * (120-ply) natural-draw rule. Reset to 0 on any capture.
   */
  halfmoveClock: number;
  /** Fullmove number, starting at 1 and incremented after each Black move. */
  fullmoveNumber: number;

  constructor(
    board?: (Piece | null)[],
    sideToMove: Color = Color.Red,
    halfmoveClock = 0,
    fullmoveNumber = 1,
  ) {
    this.board = board ?? new Array<Piece | null>(NUM_SQUARES).fill(null);
    this.sideToMove = sideToMove;
    this.halfmoveClock = halfmoveClock;
    this.fullmoveNumber = fullmoveNumber;
  }

  pieceAt(sq: Square): Piece | null {
    return this.board[sq] ?? null;
  }

  clone(): Position {
    return new Position(
      this.board.slice(),
      this.sideToMove,
      this.halfmoveClock,
      this.fullmoveNumber,
    );
  }

  /** Find the square of the given color's king, or -1 if absent (should not happen in a legal game). */
  kingSquare(color: Color): Square {
    for (let sq = 0; sq < NUM_SQUARES; sq++) {
      const p = this.board[sq];
      if (p && p.type === PieceType.King && p.color === color) return sq;
    }
    return -1;
  }

  /**
   * Apply a move in place, returning the captured piece (or null). Performs no
   * legality checking — callers use the move generator, which only yields legal
   * moves. Updates side to move and the move counters.
   */
  makeMove(move: Move): Piece | null {
    const moving = this.board[move.from];
    const captured = this.board[move.to] ?? null;
    this.board[move.to] = moving ?? null;
    this.board[move.from] = null;

    if (captured) this.halfmoveClock = 0;
    else this.halfmoveClock += 1;

    if (this.sideToMove === Color.Black) this.fullmoveNumber += 1;
    this.sideToMove = this.sideToMove === Color.Red ? Color.Black : Color.Red;
    return captured;
  }

  /**
   * Make a move and return an undo token, for search (avoids cloning the board
   * every node). Pair each `makeMoveFast` with exactly one `unmakeMove`.
   */
  makeMoveFast(move: Move): Undo {
    const undo: Undo = {
      from: move.from,
      to: move.to,
      captured: this.board[move.to] ?? null,
      prevHalfmove: this.halfmoveClock,
      prevFullmove: this.fullmoveNumber,
    };
    this.board[move.to] = this.board[move.from] ?? null;
    this.board[move.from] = null;
    if (undo.captured) this.halfmoveClock = 0;
    else this.halfmoveClock += 1;
    if (this.sideToMove === Color.Black) this.fullmoveNumber += 1;
    this.sideToMove = this.sideToMove === Color.Red ? Color.Black : Color.Red;
    return undo;
  }

  unmakeMove(undo: Undo): void {
    this.board[undo.from] = this.board[undo.to] ?? null;
    this.board[undo.to] = undo.captured;
    this.halfmoveClock = undo.prevHalfmove;
    this.fullmoveNumber = undo.prevFullmove;
    this.sideToMove = this.sideToMove === Color.Red ? Color.Black : Color.Red;
  }
}

/** Undo token produced by `makeMoveFast`. */
export interface Undo {
  readonly from: Square;
  readonly to: Square;
  readonly captured: Piece | null;
  readonly prevHalfmove: number;
  readonly prevFullmove: number;
}

/** Pretty-print a position as ASCII, from Black's back rank (top) to Red's (bottom). */
export function toAscii(pos: Position): string {
  const glyph = (p: Piece | null): string => {
    if (!p) return ".";
    const letters = ["k", "a", "b", "n", "r", "c", "p"];
    const ch = letters[p.type]!;
    return p.color === Color.Red ? ch.toUpperCase() : ch;
  };
  const lines: string[] = [];
  for (let rank = 9; rank >= 0; rank--) {
    let line = "";
    for (let file = 0; file < 9; file++) {
      line += glyph(pos.board[rank * 9 + file] ?? null);
      if (file < 8) line += " ";
    }
    lines.push(`${rank} ${line}`);
    if (rank === 5) lines.push("  ~~~~~~~~~~~~~~~~~ river ~~~~~~~~~~~~~~~~~".slice(0, 19));
  }
  lines.push("  a b c d e f g h i");
  void fileOf;
  void rankOf;
  return lines.join("\n");
}
